import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import {
  EMPTY,
  Observable,
  combineLatest,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { ViewSpec } from 'trimble-connect-workspace-api';
import { DEFAULT_SCAN_3D_STYLE } from '../scan-3d-panel/models/scan-3d-style';
import { Scan3dService } from '../scan-3d-panel/scan-3d.service';
import { PatchScandataModel } from '../scandata/scandata.actions';
import { ScandataDisplayStatus, ScandataModel } from '../scandata/scandata.models';
import { ScandataState } from '../scandata/scandata.state';
import { ClearCurrentStation } from '../station/station.actions';
import { Station } from '../station/station.models';
import { StationService } from '../station/station.service';
import { StationState } from '../station/station.state';
import { ViewData } from './host-3d.model';
import { Host3dService } from './host-3d.service';
import { ReservedViewService } from './reserved-view.service';

@Injectable({
  providedIn: 'root',
})
export class Host3dViewService {
  constructor(
    private reservedViewService: ReservedViewService,
    private host3dService: Host3dService,
    private scan3dService: Scan3dService,
    private stationService: StationService,
    private store: Store,
  ) {}

  createView(view: ViewSpec) {
    const viewId = view.id;
    if (isNil(viewId)) return EMPTY;

    return this.createReservedView().pipe(
      switchMap((reservedView) => this.reservedViewService.putView(viewId, reservedView)),
    );
  }

  updateView(view: ViewSpec) {
    const viewId = view.id;
    if (isNil(viewId)) return EMPTY;

    return this.createReservedView().pipe(
      switchMap((reservedView) => this.reservedViewService.putView(viewId, reservedView)),
    );
  }

  removeView(view: ViewSpec) {
    const viewId = view.id;
    if (isNil(viewId)) return EMPTY;

    return this.reservedViewService.deleteView(viewId);
  }

  setView(viewSpec: ViewSpec) {
    const viewId = viewSpec.id;
    if (isNil(viewId)) return EMPTY;

    return combineLatest([
      this.reservedViewService.getView(viewId),
      this.store.select(ScandataState.scandata),
    ]).pipe(
      take(1),
      switchMap(([view, scandata]) =>
        this.hideDisplayedScans(view, scandata).pipe(map(() => ({ view, scandata }))),
      ),
      filter((values): values is { view: ViewData; scandata: ScandataModel[] } => {
        return isDefined(values.view);
      }),
      map((values) => ({
        view: values.view,
        storeScans: this.getStoreScansForView(values.view, values.scandata),
      })),
      switchMap(({ view, storeScans }) =>
        this.showViewScans(storeScans).pipe(map(() => ({ view, storeScans }))),
      ),
      switchMap(({ view, storeScans }) =>
        this.styleViewScans(view, storeScans).pipe(map(() => ({ view, storeScans }))),
      ),
      switchMap(({ view }) => this.setViewCurrentStation(view)),
    );
  }

  private createReservedView(): Observable<ViewData> {
    const displayedScans$ = this.store
      .selectOnce(ScandataState.scandata)
      .pipe(
        map((scans) => scans.filter((x) => x.displayStatus === ScandataDisplayStatus.Displayed)),
      );

    const scansWithStyle$ = displayedScans$.pipe(
      withLatestFrom(this.scan3dService.getGlobalStyle()),
      map(([scans, globalStyle]) =>
        scans.map((scan) => ({ ...scan, scan3dStyle: scan.scan3dStyle ?? globalStyle })),
      ),
    );

    return scansWithStyle$.pipe(
      withLatestFrom(this.store.select(StationState.currentStation)),
      map(([scans, currentStation]) => ({
        scans,
        currentStation,
      })),
    );
  }

  private hideDisplayedScans(
    viewData: ViewData | undefined,
    scandata: ScandataModel[],
  ): Observable<unknown> {
    const scansDisplayed = scandata.filter(
      (scan) =>
        scan.displayStatus === ScandataDisplayStatus.AwaitingDisplay ||
        scan.displayStatus === ScandataDisplayStatus.Displayed,
    );

    const scansToHide = scansDisplayed.filter(
      (scan) => isNil(viewData) || viewData.scans.findIndex((x) => x.id === scan.id) === -1,
    );

    return scansToHide.length > 0 ? this.host3dService.hideScans(scansToHide) : of(true);
  }

  private getStoreScansForView(viewData: ViewData, scandata: ScandataModel[]) {
    return scandata.filter((scan) => viewData.scans.find((x) => x.id === scan.id));
  }

  private showViewScans(storeScans: ScandataModel[]): Observable<unknown> {
    const scansToShow = storeScans.filter(
      (scan) => scan.displayStatus !== ScandataDisplayStatus.Displayed,
    );

    if (scansToShow.length === 0) return of(true);

    const showScans$ = scansToShow.map((scan) => this.host3dService.showScan(scan));

    return forkJoin(showScans$);
  }

  private styleViewScans(viewData: ViewData, storeScans: ScandataModel[]): Observable<unknown> {
    // Get view scans that exist in the store (we need the scan3dStyle from the saved view)
    const viewScansInStore = viewData.scans.filter((scan) =>
      storeScans.find((x) => x.id === scan.id),
    );

    const patches = viewScansInStore.map(
      (scan) =>
        new PatchScandataModel({
          id: scan.id,
          scan3dStyle: { ...DEFAULT_SCAN_3D_STYLE, ...scan.scan3dStyle },
        }),
    );

    return this.store.dispatch(patches);
  }

  private setViewCurrentStation(viewData: ViewData): Observable<unknown> {
    if (isNil(viewData.currentStation)) {
      return this.store.dispatch(new ClearCurrentStation());
    }

    // Do nothing if view station is already displaying
    const currentStation = this.store.selectSnapshot(StationState.currentStation);
    if (currentStation?.station.id === viewData.currentStation.station.id) return of(true);

    const pointcloudId = viewData.currentStation.station.pointcloudId;
    return this.stationService.getStations(pointcloudId).pipe(
      map((stations) =>
        stations.find((station) => viewData.currentStation?.station.id === station.id),
      ),
      filter((station): station is Station => isDefined(station)),
      switchMap((station) => this.host3dService.setCurrentStation(station)),
    );
  }
}
